const STORAGE_KEY = "servicewrk_theme_prefs";

const DEFAULT_PRIMARY = "#7b046c";

/** Same gradient as NewLoginPage `.primary-btn` / `--sw-sign-in-gradient` */
export const SIGN_IN_GRADIENT =
  "linear-gradient(90deg, #7b046c 0%, #a21caf 42%, #c026d3 72%, #d61de2 100%)";

const DEFAULT_PREFS = {
  mode: "light",
  primaryColor: DEFAULT_PRIMARY,
};

function hexToRgb(hex) {
  const normalized = hex.replace("#", "");
  const full =
    normalized.length === 3
      ? normalized
          .split("")
          .map((c) => c + c)
          .join("")
      : normalized;
  const match = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(full);
  if (!match) return { r: 123, g: 4, b: 108 };
  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16),
  };
}

function mixColors(hex1, hex2, weight) {
  const c1 = hexToRgb(hex1);
  const c2 = hexToRgb(hex2);
  const w1 = weight;
  const w2 = 1 - weight;
  const rgbToHex = (r, g, b) =>
    "#" +
    [r, g, b]
      .map((value) => {
        const hex = Math.round(Math.max(0, Math.min(255, value))).toString(16);
        return hex.length === 1 ? `0${hex}` : hex;
      })
      .join("");
  return rgbToHex(
    c1.r * w1 + c2.r * w2,
    c1.g * w1 + c2.g * w2,
    c1.b * w1 + c2.b * w2,
  );
}

function rgba(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function getStoredPreferences() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw);
    return {
      mode: parsed.mode === "dark" ? "dark" : "light",
      primaryColor: DEFAULT_PRIMARY,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function savePreferences(prefs) {
  const normalized = {
    mode: prefs.mode === "dark" ? "dark" : "light",
    primaryColor: DEFAULT_PRIMARY,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  return normalized;
}

function buildThemeTokens(mode) {
  /* Deprecated — tokens live in sw-theme-tokens.css. Kept for API compatibility. */
  void mode;
  return {};
}

export function applyThemeToDocument(prefs = getStoredPreferences()) {
  const normalized = {
    mode: prefs.mode === "dark" ? "dark" : "light",
    primaryColor: DEFAULT_PRIMARY,
  };
  const root = document.documentElement;

  root.dataset.theme = normalized.mode;
  root.style.colorScheme = normalized.mode;
  root.classList.remove("v-theme--light", "v-theme--dark");
  root.classList.add(
    normalized.mode === "dark" ? "v-theme--dark" : "v-theme--light",
  );

  return normalized;
}

/** Vuetify 3 beta uses plain strings; stable 3.x uses refs — handle both. */
function isVueRef(value) {
  return (
    value != null &&
    typeof value === "object" &&
    Object.prototype.hasOwnProperty.call(value, "value")
  );
}

function readRef(value) {
  return isVueRef(value) ? value.value : value;
}

function writeRef(value, next) {
  if (isVueRef(value)) {
    value.value = next;
    return;
  }
  return next;
}

function setVuetifyGlobalThemeName(vuetify, mode) {
  const global = vuetify?.theme?.global;
  if (!global) return;

  const nameSlot = global.name;
  const replacement = writeRef(nameSlot, mode);
  if (replacement !== undefined && !isVueRef(nameSlot)) {
    global.name = replacement;
  }
}

function getVuetifyThemesRecord(vuetify) {
  const themes = vuetify?.theme?.themes;
  if (!themes) return null;
  const record = readRef(themes);
  return record && typeof record === "object" ? record : null;
}

export function applyThemeToVuetify(vuetify, prefs = getStoredPreferences()) {
  if (!vuetify?.theme) return getStoredPreferences();

  const normalized = applyThemeToDocument(prefs);
  const { primaryColor, mode } = normalized;
  const secondary = mixColors(primaryColor, "#bd58b0", 0.4);

  setVuetifyGlobalThemeName(vuetify, mode);

  const themesRecord = getVuetifyThemesRecord(vuetify);
  if (themesRecord) {
    ["light", "dark"].forEach((themeName) => {
      const theme = themesRecord[themeName];
      if (!theme?.colors) return;
      theme.colors.primary = primaryColor;
      theme.colors.secondary = secondary;
      if (themeName === "dark") {
        theme.colors.background = "#0c0e14";
        theme.colors.surface = "#1a1d27";
      } else {
        theme.colors.background = "#faf7fd";
        theme.colors.surface = "#ffffff";
      }
    });
  }

  return normalized;
}

export function setThemeMode(mode, vuetify) {
  const prefs = { mode, primaryColor: DEFAULT_PRIMARY };
  const saved = savePreferences(prefs);
  applyThemeToVuetify(vuetify, saved);
  return saved;
}

export function toggleThemeMode(vuetify) {
  const current = getStoredPreferences();
  return setThemeMode(current.mode === "dark" ? "light" : "dark", vuetify);
}

export { DEFAULT_PREFS };
